import { Component, HostListener, OnInit } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ResourceAbsence, ResourceAbsenceApprover, ResourceAbsenceApproverCandidate, ResourceAbsencePermissions,
  ResourceAbsencePlanning, ResourceAbsenceRequest
} from '../../../Models/resource-modules.models';
import { ResourceCategory, ResourceEmployee } from '../../../Models/resource.models';
import { ResourceAbsenceApiService, ResourceTeamApiService } from '../../../Services/resource-modules-api.service';
import { ResourceAccessService, ResourceTeamAccess } from '../../../Services/resource-access.service';
import { ResourceApiService } from '../../../Services/resource-api.service';

type AbsenceTab = 'calendar' | 'mine' | 'approvals';
interface CalendarDay { date: string; day: number; currentMonth: boolean; }

@Component({
  selector: 'app-resource-absences',
  templateUrl: './resource-absences.component.html',
  styleUrls: ['./resource-absences.component.css']
})
export class ResourceAbsencesComponent implements OnInit {
  tab: AbsenceTab = 'calendar';
  permissions: ResourceAbsencePermissions | null = null;
  categories: ResourceCategory[] = [];
  employees: ResourceEmployee[] = [];
  mine: ResourceAbsence[] = [];
  pending: ResourceAbsence[] = [];
  allRequests: ResourceAbsence[] = [];
  planning: ResourceAbsencePlanning[] = [];
  loading = false;
  saving = false;
  error = '';
  selectedMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  statusFilter = 'All';
  searchText = '';
  showForm = false;
  form: ResourceAbsenceRequest = { categoryId: null, title: '', start: '', end: '', notes: '' };
  private requestSequence = 0;

  /** Kalenderzellen des sichtbaren Monats – wird nur beim Monatswechsel neu aufgebaut. */
  calendarDays: CalendarDay[] = [];

  // Zeitraumauswahl im Kalender (Pointer Events, Zustand hier statt im DOM):
  // pointerdown auf einer Zelle startet die Auswahl, die Zelle unter dem Zeiger
  // wird bei pointermove per elementFromPoint ermittelt (Abwesenheits-Einträge
  // in der Zelle stören dadurch nicht), pointerup öffnet das Antragsformular.
  selecting = false;
  selectionStart = '';
  selectionEnd = '';

  get isApprover(): boolean {
    return this.permissions?.canApprove === true;
  }

  get canSelect(): boolean {
    return this.permissions?.canRequest === true && !this.showForm;
  }

  /** Ausgewählter Zeitraum, unabhängig von der Zugrichtung (auch 20. -> 15.). */
  get selectionRange(): { start: string; end: string } | null {
    if (!this.selecting || !this.selectionStart || !this.selectionEnd) { return null; }
    return this.selectionStart <= this.selectionEnd
      ? { start: this.selectionStart, end: this.selectionEnd }
      : { start: this.selectionEnd, end: this.selectionStart };
  }

  isSelected(date: string): boolean {
    const range = this.selectionRange;
    return !!range && date >= range.start && date <= range.end;
  }

  trackDay(_index: number, day: CalendarDay): string {
    return day.date;
  }

  /** Ein Handler für den ganzen Kalender; die Zelle kommt aus data-date. */
  onCalendarPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || !this.canSelect || this.selecting) { return; }
    const date = this.dateFromPoint(event.clientX, event.clientY)
      ?? this.dateFromElement(event.target as Element | null);
    if (!date) { return; }
    // Verhindert Textmarkierung und native Drag-Gesten während der Auswahl.
    event.preventDefault();
    this.selecting = true;
    this.selectionStart = date;
    this.selectionEnd = date;
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent): void {
    if (!this.selecting) { return; }
    const date = this.dateFromPoint(event.clientX, event.clientY);
    if (date && date !== this.selectionEnd) {
      this.selectionEnd = date;
    }
  }

  @HostListener('document:pointerup', ['$event'])
  onDocumentPointerUp(event: PointerEvent): void {
    if (!this.selecting) { return; }
    if (event.pointerType === 'mouse' && event.button !== 0) { return; }
    const date = this.dateFromPoint(event.clientX, event.clientY);
    if (date) {
      this.selectionEnd = date;
    }
    const range = this.selectionRange;
    this.clearSelection();
    if (range) {
      this.openForm(range.start, range.end);
    }
  }

  @HostListener('document:pointercancel')
  @HostListener('window:blur')
  onSelectionCancelled(): void {
    this.clearSelection();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selecting) { this.clearSelection(); }
  }

  private clearSelection(): void {
    this.selecting = false;
    this.selectionStart = '';
    this.selectionEnd = '';
  }

  private dateFromPoint(clientX: number, clientY: number): string | null {
    return this.dateFromElement(document.elementFromPoint(clientX, clientY));
  }

  private dateFromElement(element: Element | null): string | null {
    const cell = element?.closest('.day[data-date]') as HTMLElement | null;
    return cell?.dataset['date'] ?? null;
  }


  constructor(
    private absenceApi: ResourceAbsenceApiService,
    private resourceApi: ResourceApiService,
    private accessApi: ResourceAccessService,
    private teamApi: ResourceTeamApiService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.buildCalendar();
    this.loading = true;
    forkJoin({
      permissions: this.absenceApi.getPermissions(),
      approver: this.absenceApi.getApprover(),
      // Auswahlliste der Genehmiger; ein Fehler blockiert die übrige Seite nicht.
      candidates: this.absenceApi.getApproverCandidates().pipe(catchError(() => of([] as ResourceAbsenceApproverCandidate[]))),
      categories: this.absenceApi.getCategories(),
      // Mitarbeiterliste nur für die Namen; ein Fehler (z. B. fehlende
      // Planungsrolle) darf die Antragsfunktion nicht blockieren.
      employees: this.resourceApi.getEmployees().pipe(catchError(() => of([] as ResourceEmployee[]))),
      access: this.accessApi.getMine()
    }).subscribe({
      next: ({ permissions, approver, candidates, categories, employees, access }) => {
        this.permissions = permissions;
        this.approver = approver;
        this.approverCandidates = candidates;
        this.categories = categories.filter(c => c.countsAsAbsence);
        this.employees = employees;
        this.myTeams = access.teams;
        this.primaryTeamId = access.teams.find(t => t.isPrimaryTeam)?.teamId ?? null;
        this.reload();
      },
      error: () => {
        this.loading = false;
        this.error = 'Berechtigungen oder Stammdaten konnten nicht geladen werden.';
      }
    });
  }

  // ---- Hauptteam (nur bei mehreren Teams) --------------------------------------

  myTeams: ResourceTeamAccess[] = [];
  primaryTeamId: number | null = null;
  savingPrimary = false;

  /** Die Hauptteam-Auswahl erscheint nur bei mehreren aktiven Teams. */
  get showPrimarySelection(): boolean {
    return this.myTeams.length > 1;
  }

  get primaryLeaderHint(): string {
    const team = this.myTeams.find(t => t.teamId === this.primaryTeamId);
    return team ? `Die Teamleitung von „${team.teamName}“ ist als Urlaubsgenehmiger vorausgewählt.` : '';
  }

  // ---- Urlaubsgenehmiger -------------------------------------------------------

  /** Wert der Auswahl für „Standard“ (automatische Ermittlung, keine manuelle Festlegung). */
  readonly automaticApproverValue = '__automatic__';
  approver: ResourceAbsenceApprover | null = null;
  approverCandidates: ResourceAbsenceApproverCandidate[] = [];
  approverSearch = '';
  savingApprover = false;

  /** Aktueller Wert im Auswahlfeld: manuell gewählte Person oder „Standard“. */
  get approverSelection(): string {
    return this.approver?.isManual && this.approver.manualApproverId
      ? this.approver.manualApproverId
      : this.automaticApproverValue;
  }

  /** Beschriftung der Standard-Option – Teamleitungen: Sofortgenehmigung, sonst die Vorauswahl. */
  get automaticOptionLabel(): string {
    const a = this.approver;
    if (!a) { return 'Standard'; }
    if (a.leadsPrimaryTeam) { return 'Kein Genehmiger – automatische Genehmigung'; }
    return a.automaticApproverName
      ? `Standard: ${a.automaticApproverName}`
      : 'Standard: kein Genehmiger ermittelbar';
  }

  get filteredApproverCandidates(): ResourceAbsenceApproverCandidate[] {
    const q = this.approverSearch.trim().toLocaleLowerCase('de');
    const selected = this.approver?.manualApproverId;
    return this.approverCandidates.filter(c => c.userId === selected || !q ||
      c.name.toLocaleLowerCase('de').includes(q) || (c.email ?? '').toLocaleLowerCase('de').includes(q));
  }

  /** Ein manuell festgelegter Genehmiger, der nicht mehr in der Auswahlliste steht. */
  get missingManualApprover(): boolean {
    const id = this.approver?.manualApproverId;
    return !!id && !this.approverCandidates.some(c => c.userId === id);
  }

  changeApprover(value: string): void {
    if (this.savingApprover || !this.approver || value === this.approverSelection) { return; }
    const request$ = value === this.automaticApproverValue
      ? this.absenceApi.resetApprover()
      : this.absenceApi.setApprover(value);
    this.saveApprover(request$, value === this.automaticApproverValue
      ? 'Standard-Genehmiger wird verwendet.'
      : 'Urlaubsgenehmiger gespeichert.');
  }

  resetApprover(): void {
    if (this.savingApprover || !this.approver?.isManual) { return; }
    this.saveApprover(this.absenceApi.resetApprover(), 'Standard-Genehmiger wird verwendet.');
  }

  private saveApprover(request$: Observable<ResourceAbsenceApprover>, message: string): void {
    this.savingApprover = true;
    request$.subscribe({
      next: approver => {
        this.savingApprover = false;
        this.approver = approver;
        this.approverSearch = '';
        this.snackBar.open(message, undefined, { duration: 2500 });
        this.refreshPermissions();
      },
      error: err => {
        this.savingApprover = false;
        // Auswahl zurück auf den gespeicherten Stand.
        this.approver = this.approver ? { ...this.approver } : null;
        this.snackBar.open(typeof err?.error === 'string' && err.error ? err.error : 'Genehmiger konnte nicht gespeichert werden.', 'OK', { duration: 5000 });
      }
    });
  }

  /** Antragsrecht und Genehmiger-Hinweise hängen am Genehmiger – nach Änderungen neu laden. */
  private refreshPermissions(): void {
    this.absenceApi.getPermissions().subscribe(p => {
      const wasApprover = this.isApprover;
      this.permissions = p;
      if (wasApprover !== this.isApprover) { this.reload(); }
    });
  }

  private refreshApproverAndPermissions(): void {
    this.absenceApi.getApprover().subscribe(a => this.approver = a);
    this.refreshPermissions();
  }

  /** Zuständigkeit eines eigenen Antrags (Anzeige in „Meine Anträge“). */
  approverLabel(item: ResourceAbsence): string {
    if (item.approverSource === 'AutoApproved') { return 'Automatisch genehmigt'; }
    if (item.approverName) { return `Genehmiger: ${item.approverName}`; }
    if (!item.approverSource) { return 'Genehmiger: Teamleitung des Hauptteams'; }
    return '';
  }

  changePrimaryTeam(teamId: number): void {
    if (this.savingPrimary || teamId === this.primaryTeamId) return;
    const previous = this.primaryTeamId;
    this.primaryTeamId = teamId;
    this.savingPrimary = true;
    this.teamApi.setPrimaryTeam(teamId).subscribe({
      next: () => {
        this.savingPrimary = false;
        this.snackBar.open('Hauptteam gespeichert.', undefined, { duration: 2500 });
        // Das Hauptteam bestimmt die automatische Vorauswahl des Genehmigers.
        this.refreshApproverAndPermissions();
      },
      error: err => {
        this.savingPrimary = false;
        this.primaryTeamId = previous;
        this.snackBar.open(typeof err?.error === 'string' && err.error ? err.error : 'Hauptteam konnte nicht gespeichert werden.', 'OK', { duration: 5000 });
      }
    });
  }

  get monthLabel(): string {
    return this.selectedMonth.toLocaleDateString('de-AT', { month: 'long', year: 'numeric' });
  }

  get monthStart(): string {
    return this.formatDate(this.selectedMonth);
  }

  get monthEnd(): string {
    return this.formatDate(new Date(this.selectedMonth.getFullYear(), this.selectedMonth.getMonth() + 1, 0));
  }

  /**
   * Baut die 42 Zellen des Monatsrasters. Bewusst keine Getter-Berechnung:
   * ein Getter lieferte bei jeder Change Detection neue Objekte, wodurch
   * *ngFor die Zellen bei jedem Mausereignis neu erzeugte.
   */
  private buildCalendar(): void {
    const first = new Date(this.selectedMonth.getFullYear(), this.selectedMonth.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const days: CalendarDay[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(first.getFullYear(), first.getMonth(), 1 - offset + i);
      days.push({
        date: this.formatDate(date),
        day: date.getDate(),
        currentMonth: date.getMonth() === first.getMonth()
      });
    }
    this.calendarDays = days;
  }

  get visiblePlanning(): ResourceAbsencePlanning[] {
    const q = this.searchText.trim().toLocaleLowerCase('de');
    // Der Server liefert nur, was der Benutzer sehen darf (eigene, geleitete Teams bzw. alle für globale Planer).
    return this.planning
      .filter(a => !q ||
      this.employeeName(a.userId).toLocaleLowerCase('de').includes(q) ||
      a.title.toLocaleLowerCase('de').includes(q) ||
      (a.categoryName ?? '').toLocaleLowerCase('de').includes(q));
  }

  get filteredMine(): ResourceAbsence[] {
    return [...this.mine]
      .filter(a => this.statusFilter === 'All' || a.approvalStatus === this.statusFilter)
      .sort((a, b) => b.start.localeCompare(a.start));
  }

  get approvedCount(): number {
    return this.mine.filter(a => a.approvalStatus === 'Approved').length;
  }

  get pendingCount(): number {
    return this.mine.filter(a => a.approvalStatus === 'Pending').length;
  }

  get rejectedCount(): number {
    return this.mine.filter(a => a.approvalStatus === 'Rejected').length;
  }

  setTab(tab: AbsenceTab): void {
    if (tab === 'approvals' && !this.isApprover) { return; }
    this.tab = tab;
  }

  changeMonth(delta: number): void {
    this.selectedMonth = new Date(this.selectedMonth.getFullYear(), this.selectedMonth.getMonth() + delta, 1);
    this.clearSelection();
    this.buildCalendar();
    this.reload();
  }

  goToday(): void {
    this.selectedMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    this.clearSelection();
    this.buildCalendar();
    this.reload();
  }

  reload(): void {
    if (!this.permissions) { return; }
    const seq = ++this.requestSequence;
    this.loading = true;
    this.error = '';
    const from = `${this.monthStart}T00:00:00`;
    const to = `${this.monthEnd}T23:59:59`;
    forkJoin({
      mine: this.absenceApi.getMine(),
      planning: this.absenceApi.getPlanning(from, to),
      pending: this.isApprover ? this.absenceApi.getPending() : of([] as ResourceAbsence[]),
      all: this.isApprover ? this.absenceApi.getAll() : of([] as ResourceAbsence[])
    }).subscribe({
      next: result => {
        if (seq !== this.requestSequence) { return; }
        this.mine = result.mine;
        this.planning = result.planning;
        this.pending = result.pending;
        this.allRequests = result.all;
        this.loading = false;
      },
      error: () => {
        if (seq !== this.requestSequence) { return; }
        this.loading = false;
        this.error = 'Abwesenheiten konnten nicht geladen werden. Bitte erneut versuchen.';
      }
    });
  }

  absencesOn(date: string): ResourceAbsencePlanning[] {
    return this.visiblePlanning.filter(a => a.start.substring(0, 10) <= date && a.end.substring(0, 10) >= date);
  }

  employeeName(id: string): string {
    return this.employees.find(e => e.id === id)?.name ?? id;
  }

  /**
   * Text im Kalendereintrag: die beim Antrag angegebene Bezeichnung. Ohne eigene
   * Bezeichnung speichert das Backend den Kategorienamen als Titel (z. B. „Urlaub“);
   * fehlt auch der, bleibt „Urlaub“. Name oder ID des Benutzers erscheinen nur im Tooltip.
   */
  absenceLabel(item: ResourceAbsencePlanning): string {
    return item.title?.trim() || item.categoryName?.trim() || 'Urlaub';
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'Approved': return 'Genehmigt';
      case 'Rejected': return 'Abgelehnt';
      case 'Pending': return 'Offen';
      default: return status;
    }
  }

  formatRange(start: string, end: string): string {
    return `${this.displayDate(start)} – ${this.displayDate(end)}`;
  }

  displayDate(date: string): string {
    const [year, month, day] = date.substring(0, 10).split('-');
    return `${day}.${month}.${year}`;
  }

  openForm(start?: string, end?: string): void {
    const today = this.formatDate(new Date());
    this.form = { categoryId: this.categories[0]?.id ?? null, title: '', start: start ?? today, end: end ?? start ?? today, notes: '' };
    this.showForm = true;
  }

  submit(): void {
    if (this.saving || !this.permissions?.canRequest) { return; }
    if (!this.form.start || !this.form.end || this.form.start > this.form.end) {
      this.snackBar.open('Bitte einen gültigen Zeitraum auswählen.', 'OK', { duration: 3500 });
      return;
    }
    if (this.form.categoryId === null) {
      this.snackBar.open('Bitte eine Abwesenheitskategorie auswählen.', 'OK', { duration: 3500 });
      return;
    }
    const payload: ResourceAbsenceRequest = {
      categoryId: this.form.categoryId,
      title: this.form.title?.trim() || null,
      start: `${this.form.start}T00:00:00`,
      end: `${this.form.end}T23:59:59`,
      notes: this.form.notes?.trim() || null
    };
    this.saving = true;
    this.absenceApi.request(payload).subscribe({
      next: created => {
        this.saving = false;
        this.showForm = false;
        const message = created.approvalStatus === 'Approved'
          ? 'Abwesenheit eingetragen und genehmigt.'
          : created.approverName ? `Antrag wurde an ${created.approverName} gesendet.` : 'Antrag wurde eingereicht.';
        this.snackBar.open(message, 'OK', { duration: 3500 });
        this.reload();
      },
      error: err => this.handleActionError(err)
    });
  }

  cancelRequest(item: ResourceAbsence): void {
    if (this.saving || !item.canCancel || !confirm(`Antrag „${item.title}“ zurückziehen?`)) { return; }
    this.saving = true;
    this.absenceApi.cancel(item.id).subscribe({
      next: () => {
        this.saving = false;
        this.snackBar.open('Antrag zurückgezogen.', 'OK', { duration: 3000 });
        this.reload();
      },
      error: err => this.handleActionError(err)
    });
  }

  decide(item: ResourceAbsence, approve: boolean): void {
    if (this.saving || !item.canDecide || !this.isApprover) { return; }
    if (!confirm(`${item.userName}: Antrag wirklich ${approve ? 'genehmigen' : 'ablehnen'}?`)) { return; }
    this.saving = true;
    const request$ = approve ? this.absenceApi.approve(item.id) : this.absenceApi.reject(item.id);
    request$.subscribe({
      next: () => {
        this.saving = false;
        this.snackBar.open(approve ? 'Antrag genehmigt.' : 'Antrag abgelehnt.', 'OK', { duration: 3000 });
        this.reload();
      },
      error: err => this.handleActionError(err)
    });
  }

  private handleActionError(err: any): void {
    this.saving = false;
    const message = typeof err?.error === 'string' ? err.error
      : typeof err?.error?.message === 'string' ? err.error.message
      : 'Aktion fehlgeschlagen. Bitte erneut versuchen.';
    this.snackBar.open(message, 'OK', { duration: 4500 });
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
